import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Navigation } from '@/components/layout/Navigation';
import { Sidebar } from '@/components/layout/Sidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import TechAnalysisPanel from '@/components/analysis/TechAnalysisPanel';
import FundamentalAnalysisPanel from '@/components/analysis/FundamentalAnalysisPanel';
import { FinancialAnalystChat } from '@/components/analysis/FinancialAnalystChat';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ChevronRight, LineChart, BarChart3, ArrowDownUp, Info, ArrowLeft } from 'lucide-react';
import { StockSearchBar } from '@/components/analysis/StockSearchBar';
import { BloombergRadioButton } from '@/components/trading/BloombergRadioButton';
import { ExpandedMarketOverview } from '@/components/market/ExpandedMarketOverview';
import { NewsPanel } from '@/components/analysis/NewsPanel';
import { TradingViewChart } from '@/components/trading/TradingViewChart';


export const MarketAnalysis = () => {
  // If URL contains /analysis/:symbol, honour it; fall back to SPY.
  const params = useParams<{ symbol?: string }>();
  const initialSymbol = (params.symbol || 'SPY').toUpperCase();

  const [selectedSymbol, setSelectedSymbol] = useState<string>(initialSymbol);
  const [analysisHistory, setAnalysisHistory] = useState<Array<{ symbol: string, timestamp: Date }>>([]);
  const [activeTab, setActiveTab] = useState<string>("technical");
  const [showChat, setShowChat] = useState<boolean>(true);
  const navigate = useNavigate();
  const [isOverviewExpanded, setIsOverviewExpanded] = useState<boolean>(false);

  // Keep component state in sync when route param changes (e.g., via browser nav)
  useEffect(() => {
    if (params.symbol && params.symbol.toUpperCase() !== selectedSymbol) {
      setSelectedSymbol(params.symbol.toUpperCase());
    }
  }, [params.symbol]);

  // Add symbol to history when it changes and update URL
  useEffect(() => {
    if (selectedSymbol) {
      // Update URL when symbol changes (without triggering the above effect)
      if (!params.symbol || params.symbol.toUpperCase() !== selectedSymbol) {
        navigate(`/analysis/${selectedSymbol}`, { replace: true });
      }
      
      setAnalysisHistory(prev => {
        // Don't add duplicate entries for the same symbol in a row
        if (prev.length > 0 && prev[0].symbol === selectedSymbol) {
          return prev;
        }
        return [{ symbol: selectedSymbol, timestamp: new Date() }, ...prev.slice(0, 4)];
      });
    }
  }, [selectedSymbol, navigate, params.symbol]);

  // Handle symbol change from search bar
  const handleSymbolChange = (symbol: string) => {
    setSelectedSymbol(symbol.toUpperCase());
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div className="bg-gray-50 min-h-screen flex flex-col w-full">
      <Navigation 
        activeView="chat"
        setActiveView={() => {}}
        showRoles={false}
        setShowRoles={() => {}}
      />

      <div className="flex-1 flex">
        <div className="hidden md:block">
          <Sidebar />
        </div>

        <main className="flex-1 relative">
          {/* BloombergRadioButton component */}
          <BloombergRadioButton />
          
          <div className="p-6 pb-16 w-full max-w-[1920px] mx-auto">
            <div className="mb-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => navigate(-1)}
                  className="rounded-full h-8 w-8 mr-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span className="sr-only">Back</span>
                </Button>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Market Analysis</h1>
                  <p className="text-gray-500 mt-1">
                    Explore market data, analyze trends, and get AI-powered insights
                  </p>
                </div>
              </div>
              
            <StockSearchBar
              onSymbolSelect={handleSymbolChange}
              selectedSymbol={selectedSymbol}
            />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Content - Takes up 2/3 of the space on large screens */}
              <div className="lg:col-span-2 space-y-6">
                <Card className="w-full">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-xl font-semibold">
                        {selectedSymbol} Analysis
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="technical" className="flex items-center gap-2">
                          <LineChart className="w-4 h-4" />
                          Technical
                        </TabsTrigger>
                        <TabsTrigger value="fundamental" className="flex items-center gap-2">
                          <BarChart3 className="w-4 h-4" />
                          Fundamental
                        </TabsTrigger>
                      </TabsList>
                      <TabsContent value="technical" className="mt-4">
                        <TechAnalysisPanel symbol={selectedSymbol} />
                      </TabsContent>
                      <TabsContent value="fundamental" className="mt-4">
                        <FundamentalAnalysisPanel symbol={selectedSymbol} />
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>
                {/* TradingView Chart */}
                <Card className="w-full h-[400px] rounded-lg">
                  <CardContent className="p-0 h-full">
                    <TradingViewChart symbol={selectedSymbol} height="100%" width="100%" />
                  </CardContent>
                </Card>

                {/* Recent News */}
                <Card className="w-full">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-xl font-semibold">Recent News</CardTitle>
                    <CardDescription>
                      Latest market updates and news for {selectedSymbol}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <NewsPanel symbol={selectedSymbol} />
                  </CardContent>
                </Card>
              </div>

              {/* Right Sidebar - Takes up 1/3 of the space on large screens */}
              <div className="space-y-6">
                {/* Analyst Chat Interface */}
                <Card className="bg-white shadow-sm w-full">
                  <CardHeader className="pb-3 flex flex-row items-center justify-between">
                    <CardTitle className="text-lg font-semibold">Financial Analyst</CardTitle>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setShowChat(!showChat)}
                      className="h-7 w-7 p-0 rounded-full"
                    >
                      <ChevronRight className={`h-4 w-4 transition-transform ${showChat ? 'rotate-90' : ''}`} />
                    </Button>
                  </CardHeader>
                  <CardContent className={`transition-all overflow-hidden ${showChat ? 'max-h-[500px]' : 'max-h-0 p-0'}`}>
                    <FinancialAnalystChat selectedSymbol={selectedSymbol} />
                  </CardContent>
                </Card>

                {/* Market Overview Card */}
                <div className="w-full">
                  <ExpandedMarketOverview
                    onSymbolSelect={handleSymbolChange}
                  />
                </div>

                {/* Recently Viewed */}
                <Card className="w-full">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-semibold">Recent Analysis</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {analysisHistory.length > 0 ? (
                        analysisHistory.map((item, index) => (
                          <div 
                            key={index} 
                            className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-gray-50 cursor-pointer"
                            onClick={() => handleSymbolChange(item.symbol)}
                          >
                            <span className="font-medium">{item.symbol}</span>
                            <span className="text-sm text-gray-500">
                              {item.timestamp.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="text-center text-gray-500 py-4 flex items-center justify-center">
                          <Info className="h-4 w-4 mr-2" />
                          <span>No recent symbols</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Expanded Market Overview Dialog */}
                <Dialog open={isOverviewExpanded} onOpenChange={setIsOverviewExpanded}>
                  <DialogContent className="max-w-4xl">
                    <DialogHeader>
                      <DialogTitle>Comprehensive Market Overview</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                      <ExpandedMarketOverview 
                        onSymbolSelect={(symbol) => {
                          handleSymbolChange(symbol);
                          setIsOverviewExpanded(false);
                        }}
                      />
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default MarketAnalysis;
